<script setup lang="ts">
import TimeClockContentCard from './TimeClockContentCard.vue';

import { TIMECLOCK_ENDPOINTS } from '../../api/time-clock.service';
import { Camera } from '../../interfaces/camera.interface';
import { Notification, NotificationType } from '../../interfaces/notification.interface';
import { ScanResult } from '../../interfaces/scan-result.interface';
import { TimeClockCardResponse } from '../../interfaces/time-clock-card-response.interface';

import * as Sentry from '@sentry/vue';
import QrScanner from 'qr-scanner';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { api } from '../../helpers/axiosHelper';

const { t } = useI18n();

const props = defineProps({
   isOffline: { type: Boolean, default: false },
   syncId: { type: String, required: true },
});

const emits = defineEmits<{
   setNotification: [Notification];
}>();

// #region offline
watch(
   () => props.isOffline,
   () => {
      if (props.isOffline) destroyScanner();
   },
);
// #endregion

// #region set and destroy
onMounted(() => {
   setupScanner();
});

onBeforeUnmount(() => {
   destroyScanner();
});
// #endregion

// #region setup scanner
const stream_of_qr_scanner = ref<any>(null);
const is_camera_permission_denied = ref<boolean>(false);

const available_cameras = ref<Camera[]>([]);

async function setupScanner() {
   const VIDEO_ELEMENT = document.getElementById('qr-scanner');
   if (!VIDEO_ELEMENT) return;

   stream_of_qr_scanner.value = new QrScanner(
      VIDEO_ELEMENT as HTMLVideoElement,
      (result: ScanResult) => handleSuccessfullScan(result.data),
      {
         onDecodeError: (decodeError) => {
            if (typeof decodeError === 'string') {
               return; // This throws when it doesn't recognize a QR code, we don't want to log this
            } else {
               Sentry.captureException(new Error(decodeError.name, { cause: decodeError.cause }), { data: { ...decodeError } });
            }
         },
         calculateScanRegion: () => {
            return {
               x: VIDEO_ELEMENT.offsetWidth - VIDEO_ELEMENT.offsetWidth * 1.5,
               y: VIDEO_ELEMENT.offsetHeight - VIDEO_ELEMENT.offsetHeight * 1.5,
               width: VIDEO_ELEMENT.offsetWidth * 3,
               height: VIDEO_ELEMENT.offsetWidth * 3,
               downScaledWidth: VIDEO_ELEMENT.offsetWidth * 3,
               downScaledHeight: VIDEO_ELEMENT.offsetWidth * 3,
            };
         },
         preferredCamera: 'user',
         returnDetailedScanResult: true,
         maxScansPerSecond: 5,
      },
   );

   stream_of_qr_scanner.value
      .start()
      .then(async () => {
         // List cameras after the scanner started to avoid listCamera's stream and the scanner's stream being requested
         // at the same time which can result in listCamera's unconstrained stream also being offered to the scanner.
         // Note that we can also start the scanner after listCameras, we just have it this way around in the demo to
         // start the scanner earlier.

         available_cameras.value = await QrScanner.listCameras(true);
      })
      .catch((error: string) => {
         if (error === 'Camera not found.') {
            emits('setNotification', {
               id: 'is_infinite',
               message: t('camera_permission_denied'),
               type: NotificationType.Error,
               is_infinite: true,
            });
         } else {
            Sentry.captureException(new Error(error));
         }

         is_camera_permission_denied.value = true;
      });

   QrScanner.hasCamera().catch(() => {
      destroyScanner();
   });
}
// #endregion

// #region swap camera
const is_camera_swapping = ref<boolean>(false);
const current_camera_view = ref<string>('user');
async function swapCamera() {
   is_camera_swapping.value = true;
   current_camera_view.value = current_camera_view.value === 'user' ? 'environment' : 'user';

   await stream_of_qr_scanner.value.setCamera(current_camera_view.value);
   is_camera_swapping.value = false;
}
// #endregion

// #region helpers
// function checkIfCameraStreamIsActive() {
//    navigator.mediaDevices
//       .getUserMedia({ video: true })
//       .then(() => {
//          if (is_camera_permission_denied.value) {
//             setupScanner();
//             is_camera_permission_denied.value = false;
//          }
//       })
//       .catch(async (error) => {
//          is_camera_permission_denied.value = true;

//          await destroyScanner();
//          await setupScanner();
//       });
// }

async function destroyScanner() {
   await stream_of_qr_scanner.value.destroy();
   stream_of_qr_scanner.value = null;
}
// #endregion

// #region handle and post
const last_posted_url = ref<String>('');
async function handleSuccessfullScan(decoded_qr: string) {
   if (decoded_qr === '') return;

   if (last_posted_url.value === decoded_qr) return;
   last_posted_url.value = decoded_qr;

   const NOTIFICATION_ID = Math.random().toString(36).substring(7);
   emits('setNotification', {
      id: NOTIFICATION_ID,
      message: t('qr_scanning'),
      type: NotificationType.Info,
   });

   registerTimeregistration(decoded_qr, NOTIFICATION_ID);

   setTimeout(() => {
      last_posted_url.value = '';
   }, 1000 * 5);
}

const show_card_timeout = ref<any>(null);
const timeclock_content_card = ref<TimeClockCardResponse>();
function registerTimeregistration(decoded_qr_to_post: string, notification_id: string) {
   timeclock_content_card.value = undefined;
   if (show_card_timeout.value) clearTimeout(show_card_timeout.value);

   api.post(TIMECLOCK_ENDPOINTS.POST(props.syncId), { badge_uuid_url: decoded_qr_to_post })
      .then((data) => {
         timeclock_content_card.value = data.data;

         emits('setNotification', {
            id: notification_id,
            message: t('qr_success'),
            type: NotificationType.Success,
         });
      })
      .catch((error) => {
         const ERROR_MESSAGE = error.response?.data?.message;

         emits('setNotification', {
            id: notification_id,
            message: ERROR_MESSAGE ? ERROR_MESSAGE : t('qr_not_recognized'),
            type: NotificationType.Error,
         });
      });

   show_card_timeout.value = setTimeout(() => {
      timeclock_content_card.value = undefined;
   }, 1000 * 10);
}
// #endregion
</script>

<template>
   <div class="scan">
      <div class="scan__video">
         <video
            id="qr-scanner"
            :class="{
               '-blurred': !timeclock_content_card && !isOffline && !is_camera_permission_denied,
               '-no-camera-stream': is_camera_permission_denied || isOffline,
            }"
         />
         <i class="fas fa-camera-rotate scan__video__swap" :class="{ '-disabled': is_camera_swapping }" @click="swapCamera" />
         <i v-if="is_camera_permission_denied || isOffline" class="scan__video__no-camera-stream fas fa-camera-slash" />
      </div>

      <div class="scan__content">
         <TimeClockContentCard v-if="timeclock_content_card" :content="timeclock_content_card" />
         <div v-else class="scan__content__placeholder">
            <i class="fa-thin fa-barcode-read"></i>
            <span>{{ t('scan_to_register') }}</span>
         </div>
      </div>
   </div>
</template>

<style lang="scss" scoped>
.scan {
   display: flex;
   flex-direction: row-reverse;
   justify-content: center;
   align-items: center;

   width: calc(100% - var(--nxt-gutter-large) * 2);
   height: calc(100% - var(--nxt-gutter-large));

   padding: 0 calc(var(--nxt-gutter-large) * 2);
}

.scan__video,
.scan__content {
   z-index: 1;
   width: 50%;
   height: 100%;
   overflow: hidden;
}

// MediaQueries { SM } = 768px
@media (max-width: 768px) {
   .scan {
      flex-direction: column;
   }

   .scan__video,
   .scan__content {
      width: 100%;
      height: 50%;
   }
}

.scan__video {
   position: relative;
}

.scan__video__swap {
   position: absolute;
   right: 0;
   top: 0;

   padding: var(--nxt-gutter);
   border-radius: 0 0 0 var(--nxt-radius);

   color: var(--nxt-white);
   background-color: var(--nxt-plan-main);

   &.-disabled {
      cursor: not-allowed;
      opacity: 0.5;
   }
}

.scan__video video {
   z-index: 1;
   object-fit: cover;
   width: 100%;
   height: 100%;

   &.-blurred {
      filter: blur(20px);
      -webkit-filter: blur(20px);
      -moz-filter: blur(20px);
      -o-filter: blur(20px);
      -ms-filter: blur(20px);
   }

   &.-no-camera-stream {
      position: relative;
      background-color: var(--nxt-light-grey);
   }
}

.scan__video__no-camera-stream {
   z-index: 2;
   position: absolute;
   left: 50%;
   top: 50%;
   transform: translate(-50%, -50%);

   font-size: 50px;
}

.scan__content {
   display: flex;
   justify-content: center;
   align-items: center;
}

.scan__content__card {
   height: fit-content;
   padding: var(--nxt-gutter);

   border-radius: var(--nxt-radius);

   background-color: white;
}

.scan__content__placeholder {
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: var(--nxt-gutter-large);
   color: var(--nxt-white);

   i {
      font-size: 4rem;
   }
}
</style>
